import React, { memo, useState, useEffect, useRef, useMemo } from "react";
import {Box, Typography, Tooltip, Chip, Slider} from "@mui/material";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import { getSubRoot, thumbnailMap } from "@/utils/utils";
import infoIcon from "../../icon/info.svg";
import playIcon from "../../icon/play.svg";
import pauseIcon from "../../icon/pause.svg";
import editIcon from "../../icon/edit2.svg";
import download from '../../icon/download.svg';
import maximaizeIcon from "../../icon/maximaize.svg";
import minimizeIcon from "../../icon/minimize.svg";
import deleteIcon from "../../icon/delete.svg";
import downloadVideo from '../../utils/utils';
import { useSelectedContent } from "@/contexts/SelectedContentContext";
import { useKeyMomentActiveFilters } from "@/contexts/application/KeyMomentActiveFilters";
import { useClipActiveFilters } from "@/contexts/application/ClipActiveFilters";
import { useNavigate } from "react-router-dom";
import SmoothJumpVideoPlayer from "./SmoothJumpVideoPlayer/SmoothJumpVideoPlayer";
import { usePageSettings } from "@/contexts/PageSettingsContext";
import { fsIconImgSx, iconImgSx, iconButtonSx } from "@/constants/styles";
import VideoPlayer from "@/custom-components/VideoPlayer/VideoPlayer";
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { EDITOR_CONTENT_TYPE } from "@/types/editorTypes";
import {
    CLIP_PAGE_NAME,
    CONTENT_AGGREGATION_PAGE_NAME,
    CURRENT_SPORT,
    KEY_MOMENT_PAGE_NAME, LIBRARY_PAGE_NAME,
    SUPPORTED_CONTENTS, SUPPORTED_SPORTS, EVENTS_PAGE_NAME
} from "@/constants/globalConstants";
import { useSelectedCard } from "@/contexts/SelectedCardContext";
import { PANEL_MODE_UPDATE } from "@/types/generic";
import { useEditorKeyMoments } from "@/contexts/EditorKeyMomentsCuts";
import { useEditorialAssets } from "@/contexts/EditorialAssetsContext";
import { enqueueSnackbar } from "notistack";
import WipeCard from "./wipeCard";
import ClipCardRetro from "./clipCardRetro";
import StaticStarRating from "./StaticStarRating";
import VideoJSPlayer from "./CommonComponents/VideoJSPlayer";
import {useEditState} from "@/contexts/editor/EditStateContext";
import {useIntl} from "react-intl";
import {Sports} from "@mui/icons-material";
import { useRenderProfiler, profEffect } from "@/utils/renderProfiler";
import {useDimensionCardProvider, DimensionCardsMulti} from "@/contexts/application/DimensionCardsProvider";
import FullScreenControlPanel from "./FullScreenControlPanel";
import {deleteLogicClip} from "@/queries/clipQueries";
import {deleteKeyMoment, deleteKeyMomentCutMaster} from "@/queries/keyMomentQueries";
import StyledPopper from "@/pages/custom-components/StyledPopper";
import {trackAction} from "@/queries/tracking";
import {TRACKING_CONTENT_TYPE, UI_TRACKING_ACTION} from "@/types/trackingTypes";
import {useSuggestedCuts} from "@/contexts/SuggestedCutsContext";
import {formatPlayTime} from "@/utils/formatPlayTime";

export const cardTitleDict = [
    {km: 'serve', title: 'Ace'},
    {km: 'reception', title: 'Ace'},
    {km: 'attack', title: 'Attack'},
    {km: 'servizio', title: 'Ace'},
    {km: 'muro', title: 'Block'},
    {km: 'alzata', title: 'Set'},
    {km: 'difesa', title: 'Defense'},
    {km: 'freeball', title: 'Freeball'},
    {km: 'invasione', title: 'Invasion'},
    {km: 'sostituzione', title: 'Substitution'},
    {km: 'timeout', title: 'Timeout'}
]

export const getCardTitle = (card) => {
    if(CURRENT_SPORT === SUPPORTED_SPORTS.VOLLEY && card.title === 'RALLY_POINT'){
        const normTags = card.tags ? card.tags.map(tag => tag.toLowerCase()) : []
        // Restituisce il title se tra i tags c'è uno tra i "km" ovvero keymoment
        const mappedTitle = cardTitleDict.find(el => normTags.includes(el.km))?.title
        if(mappedTitle)
            return mappedTitle
        // C'è la possibilità che tra i tags c'è direttamente il title, quindi, in quel caso, restituisco direttamente quello
        else {
            const mappedDirectTitle = cardTitleDict.find(el => normTags.includes(el.title.toLowerCase()))?.title
            if(mappedDirectTitle)
                return mappedDirectTitle
            else
                return card.title
        }
    }else{
        return card.title
    }
}

function ClipCard({
    collectionId, card, clipType, editFlag, deleteFlag = true, pinned, togglePinClip,
    selected, suggested, toggleSelect, calcMinWidth = false,
    infoIconFlag, clipProgress, multiSelection = false, smaller = false,
    showedBack = true, unifyPlaybackMutex = false, hasWarning = false,
    nameEditDisabled, sortable = false, interactive = true
}) {
    useRenderProfiler("ClipCard", {
        collectionId, card, clipType, editFlag, pinned, togglePinClip,
        selected, suggested, toggleSelect, calcMinWidth, infoIconFlag,
        clipProgress, multiSelection, smaller, showedBack, unifyPlaybackMutex, interactive,
    });

    const [openPlayer, setOpenPlayer] = useState(false);
    const [openPopper, setOpenPopper] = useState(false); // Popper per il delete to confirm
    const [play, setPlay] = useState(false);

    const playerRef    = useRef(null);
    const jumpPlayerRef = useRef(null);

    const { contentType, setSelectedEventName, setSelectedEventId } = useSelectedContent();
    const { setEditorKeyMoments } = useEditorKeyMoments();
    const { resetSelectionContext, setPanelMode, setLogicClip, getSelectedKeyMomentCutId, setContentType, setSelectedCard, removeClipCard, removeKeyMomentCard  } = useSelectedCard();
    const { volume, muted, setVolume, setMuted } = usePageSettings();
    const { resetKeyMomentActiveOption } = useKeyMomentActiveFilters();
    const { resetClipActiveOption } = useClipActiveFilters();
    const { resetEditorialAssetSelection, setEditorialAssets } = useEditorialAssets();
    const { emptyWindowLandmarks, setLocalKeyMoment } = useEditState()
    const { clearSuggestedCuts } = useSuggestedCuts()
    const navigate = useNavigate();

    const anchorRef = React.useRef(null);
    const intl = useIntl();

    const {dimensionCard: globalDimensionCard} = useDimensionCardProvider()
    // Le card "smaller" (es. drawer dei selected) devono restare sempre alla dimensione
    // small, a prescindere dall'opzione Small/Medium/Large scelta nella griglia principale.
    const dimensionCard = smaller ? DimensionCardsMulti.SMALL : globalDimensionCard

    const handleSliderChange = (event, newValue) => {
        setVolume(newValue);
    };

    const aspectRatio = useMemo(
        () => (card?.materializations?.length > 0 ? card.materializations[0] : null),
        [card],
    );

    useEffect(() => profEffect("effect:ClipCard(volume/muted)", () => {
        const shouldUnmute = !muted;
        const v01 = Math.max(0, Math.min(1, (Number(volume) || 0) / 100));

        const p1 = jumpPlayerRef.current;
        if (p1) {
            p1.setMuted?.(!shouldUnmute);
            if (shouldUnmute) p1.setVolume?.(v01);
        }

        const p2 = playerRef.current;
        if (p2) {
            p2.setMuted?.(!shouldUnmute);
            if (shouldUnmute) p2.setVolume?.(v01);
        }
    }), [muted, volume]);

    const program = useMemo(() => {
        const kms = Array.isArray(card?.key_moments) ? card.key_moments : [];
        const fromKeyMoments = kms
            .filter((km) => typeof km?.url === "string" && km.url.length > 0)
            .map((km) => ({ src: km.url, cuts: Array.isArray(km?.cuts) ? km.cuts : [] }));
        if (fromKeyMoments.length > 0) return fromKeyMoments;
        const fallbackUrl = typeof card?.url === "string" ? card.url : "";
        return fallbackUrl ? [{ src: fallbackUrl, cuts: Array.isArray(card?.cuts) ? card.cuts : [] }] : [];
    }, [card]);

    const hasPlayableVideo = program.length > 0 && !!program[0]?.src;

    const cardFormatPlayTime = () => {
        if (clipType === 'clips' || contentType === EDITOR_CONTENT_TYPE.CLIP || location.pathname.includes('/clips')) {
            const duration = card.materializations && card.materializations[0] ? card.materializations[0].duration : card.duration;
            return String(Math.floor(duration / 60)).padStart(2, '0') + ':' + String(Math.floor(duration % 60)).padStart(2, '0');
        }

        switch (CURRENT_SPORT) {
            case SUPPORTED_SPORTS.FOOTBALL:
                if(['FIRST_HALF', 'SECOND_HALF', 'FIRST_PERIOD_OF_EXTRA_TIME', 'SECOND_PERIOD_OF_EXTRA_TIME', 'PENALTY_SHOOT_OUT'].includes(card.period)){
                    return formatPlayTime.FOOTBALL(card.game_time || card.global_seconds, card.period);
                } else {
                    return ''
                }

            case SUPPORTED_SPORTS.VOLLEY:
                return card.play_time;
        }
    };

    const handleToggleFullscreen = (e) => {
        e.stopPropagation();
        card?.materializations?.length > 0
            ? playerRef.current?.toggleFullscreen()
            : jumpPlayerRef.current?.toggleFullscreen();
    };

    const onEditClick = (e) => {
        e.stopPropagation();
        setLocalKeyMoment(null)
        if (clipType === 'clips') {
            const targetPanel = card.content_type ? card.content_type.toLowerCase() : SUPPORTED_CONTENTS[CURRENT_SPORT].CLIP.toLowerCase();
            const basePath = `${getSubRoot(window.location.pathname)}/${CONTENT_AGGREGATION_PAGE_NAME}/${targetPanel}`;
            resetClipActiveOption();
            resetKeyMomentActiveOption();
            setLogicClip(card);
            emptyWindowLandmarks()
            clearSuggestedCuts()
            setPanelMode(PANEL_MODE_UPDATE);
            resetSelectionContext();
            resetEditorialAssetSelection();
            setEditorialAssets([])
            setContentType(card.content_type)
            navigate(basePath);
        } else if (clipType === 'key_moments') {
            const selectedKeyMomentCutId = getSelectedKeyMomentCutId(card.id);
            emptyWindowLandmarks()
            clearSuggestedCuts()
            if (card.event) {
                setSelectedEventId(card.event.id);
                setSelectedEventName(card.event.name);
            }
            if (selectedKeyMomentCutId) {
                setEditorKeyMoments([{ key_moment_id: card.id, key_moment_cut_id: selectedKeyMomentCutId }]);
            } else {
                setEditorKeyMoments(card.key_moments.map(cardCut => ({ key_moment_id: card.id, key_moment_cut_id: cardCut.cut_id })));
            }
            const basePath = getSubRoot(window.location.pathname);
            const ct = (window.location.pathname.includes(KEY_MOMENT_PAGE_NAME) || window.location.pathname.includes(EVENTS_PAGE_NAME))
                ? KEY_MOMENT_PAGE_NAME
                : CLIP_PAGE_NAME;
            navigate(`${basePath}/${ct}/editor?id=${card.id}`);
        } else {
            console.error("Content Not supported for editing");
        }
    };

    // ── Render slots ──────────────────────────────────────────────────────────

    const renderVideo = () => (
        <>
            {card?.materializations?.length > 0 && (
                <>
                    <Box sx={{ position: 'absolute', top: 5, right: 5, zIndex: 999 }}>
                        <Chip
                            label={aspectRatio ? String(getAspectRatio(aspectRatio.aspect_ratio)) : card?.materializations?.[0] ? String(getAspectRatio(card.materializations[0].aspect_ratio)) : ''}
                            sx={{
                                fontSize: `${calcMinWidth ? 11 : 11*dimensionCard}px`,
                                background: '#000000',
                                height: 'auto',
                                '& .MuiChip-label': {
                                    padding: `${calcMinWidth ? 4 : 4*dimensionCard}px ${calcMinWidth ? 4 : 4*dimensionCard}px`,
                                }
                             }}
                        />
                    </Box>
                    <VideoPlayer
                        url={aspectRatio?.preview_url}
                        ref={playerRef}
                        coordinatorId={unifyPlaybackMutex ? `clip-${collectionId}__${card.id}` : undefined}
                        renderControls={({ state }) => {
                            if (!state.isFullscreen) return null;
                            return (
                                <Box sx={{position: 'absolute', pointerEvents: 'none',  bottom: 0, left: 0, right: 0, height: '80px', gap: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3, pb: 1, background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 60%, transparent 100%)', zIndex: 10}}>

                                    {/* CONTROLLO VOLUME con slider */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            mr: 'auto',
                                            pointerEvents: 'auto',
                                            transform: 'translateY(2px)',
                                            '&:hover .volume-slider-box': {
                                                width: '80px',
                                                opacity: 1,
                                                visibility: 'visible',
                                                ml: 1,
                                            }
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        onPointerDown={(e) => e.stopPropagation()}
                                    >
                                        {/* BOTTONE ICONA */}
                                        <IconButton
                                            sx={{ ...iconButtonSx, p: 1 }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setMuted(prev => !prev);
                                            }}
                                        >
                                            {muted ? <VolumeOffIcon color="disabled" /> : <VolumeUpIcon color="disabled" />}
                                        </IconButton>

                                        <Box
                                            className="volume-slider-box"
                                            sx={{
                                                width: 0,
                                                opacity: 0,
                                                visibility: 'hidden',
                                                transition: 'all 0.2s ease-in-out',
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Slider
                                                disabled={!!muted}
                                                value={volume}
                                                onChange={handleSliderChange}
                                                min={0}
                                                max={100}
                                                size="small"
                                                sx={{
                                                    color: 'white',
                                                    padding: '13px 0',
                                                    '& .MuiSlider-thumb': {
                                                        width: 12,
                                                        height: 12
                                                    }
                                                }}
                                            />
                                        </Box>
                                    </Box>


                                    <Box sx={{
                                        display: 'flex',
                                        gap: 2,
                                        position: 'absolute',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        pointerEvents: 'auto'
                                    }}>
                                        {/* FULLSCREEN (EXIT) */}
                                        <IconButton
                                            sx={{...iconButtonSx, pointerEvents:'auto'}}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                playerRef.current?.toggleFullscreen();
                                            }}
                                        >
                                            <Box component="img" src={minimizeIcon} alt="exit fullscreen" sx={fsIconImgSx} />
                                        </IconButton>

                                        {/* PLAY / PAUSE */}
                                        <IconButton
                                                sx={{...iconButtonSx, pointerEvents:'auto'}}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (state.isPlaying) playerRef.current?.pause();
                                                    else playerRef.current?.play();
                                                }}
                                        >
                                                <Box component="img" src={state.isPlaying ? pauseIcon : playIcon} alt={state.isPlaying ? "pause" : "play"} sx={fsIconImgSx} />
                                        </IconButton>

                                        {/* TODO: da pensare se mettere il timecode (durata attuale / durata vera e propria) */}
                                        {/* EDIT */}
                                        {editFlag && (
                                            <IconButton sx={{...iconButtonSx, pointerEvents:'auto'}} onClick={onEditClick}>
                                                <Box component="img" src={editIcon} alt="edit" sx={fsIconImgSx} />
                                            </IconButton>
                                        )}
                                    </Box>

                                </Box>
                            );
                        }}
                    />
                </>
            )}
            {hasPlayableVideo && (!card?.materializations || card?.materializations?.length === 0) && (
                <SmoothJumpVideoPlayer
                    ref={jumpPlayerRef}
                    program={program}
                    poster={card.thumbnail || thumbnailMap[getSubRoot(window.location.pathname)]}
                    onPlayStateChange={setPlay}
                    sx={{ transform: "scale(1.02)", transition: "opacity 180ms ease" }}
                    renderControls={({ state }) => {
                        if (!state.isFullscreen) return null;
                        return <FullScreenControlPanel
                            jumpPlayerRef={jumpPlayerRef}
                            state={state}
                            card={card}
                            editFlag={editFlag}
                            onEditClick={onEditClick}
                            formatPlayTime={cardFormatPlayTime}
                            volume={volume}
                            handleSliderChange={handleSliderChange}
                            muted={muted}
                            setMuted={setMuted}
                            clipType={clipType}
                        />
                    }}
                />
            )}
        </>
    );

    const renderSummary = () => (
        <>
            <Grid container size={{ xs: 12 }} sx={{ height: "30%" }}>
                <Grid size={{ xs: 8 }} sx={{ height: "100%", justifyContent: "flex-start", alignItems: "flex-end", minWidth: 0, overflow: "hidden" }}>
                    <Typography
                        fontSize={11}
                        sx={{
                            fontFamily: "IBM Plex Sans, sans-serif !important",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {getCardTitle(card)
                            ? intl.formatMessage({
                                id: getCardTitle(card),
                                defaultMessage: getCardTitle(card)
                            })
                            : ''
                        }
                    </Typography>
                </Grid>
                { !(card.period === 'PRE_MATCH' || card.period === 'POST_MATCH') &&
                <Grid container size={{ xs: 4 }} sx={{ height: "100%", justifyContent: "flex-end", alignItems: "flex-end" }}>
                    <Typography fontSize={11} sx={{ textAlign: "right", fontFamily: "IBM Plex Sans, sans-serif !important" }}>{cardFormatPlayTime()}</Typography>
                </Grid>
                }
            </Grid>
            <Grid container size={{ xs: 12 }} sx={{ height: "30%" }}>
                <Grid size={{ xs: 9 }} sx={{ height: "100%", justifyContent: "flex-start", alignItems: "flex-end", minWidth: 0, overflow: "hidden" }}>
                    <Typography
                        fontSize={10}
                        sx={{
                            fontFamily: "IBM Plex Sans, sans-serif !important",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {card.eyebrow}
                    </Typography>
                </Grid>
                <Grid container size={{ xs: 3 }} sx={{ height: "100%", justifyContent: "flex-end", alignItems: "flex-end" }}>
                    <Tooltip title={card.rating} placement="top">
                        <Grid container size={{ xs: 3 }} sx={{ justifyContent: "flex-end", alignItems: "flex-end" }}>
                            <StaticStarRating value={card.rating} fontSize={smaller ? 8 : 10} />
                        </Grid>
                    </Tooltip>
                </Grid>
            </Grid>
        </>
    );

    const handleDeleteKeyMoment = async (cardId, option) => {
        if(option === 'NO') {
            setOpenPopper(false)
        }
        else {
            try {
                enqueueSnackbar(`Delete ${contentType === 'clip' ? 'clip' : ' key moment'} Request Started`)
                contentType === 'clip' ?  await deleteLogicClip(cardId) : await deleteKeyMomentCutMaster(cardId)
                enqueueSnackbar(`${contentType === 'clip' ? 'Clip' : 'Key moment'} Deleted Successfully`)
                setOpenPopper(false)

                trackAction(UI_TRACKING_ACTION.CONTENT_DELETE, {
                    content_id: card.id,
                    content_type: clipType === "key_moments" ? TRACKING_CONTENT_TYPE.KEY_MOMENT : TRACKING_CONTENT_TYPE.CLIP,
                    name: card.title,
                    event_name: card.eyebrow,
                    competition_name:card.rubric
                })

                const path = location.pathname;
                const isKeyMoments = path.includes(KEY_MOMENT_PAGE_NAME);
                const isClips = path.includes(CLIP_PAGE_NAME);
                const isLibrary = path.includes(LIBRARY_PAGE_NAME);
                const isContentAggregation = path.includes(CONTENT_AGGREGATION_PAGE_NAME)

                if(isLibrary || isContentAggregation){
                    setSelectedCard(prev => prev.filter(c => c.id !== card.id))
                }else if(isKeyMoments){
                    removeKeyMomentCard(card.id)
                }else if(isClips){
                    removeClipCard(card.id)
                }

            }catch (e) {
                console.error(e)
                enqueueSnackbar(`Problem Deleting the ${contentType === 'clip' ? 'clip' : ' key moment'}`)
            }
        }

    }


    const renderDetails = ({ setFlipped }) => (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", width: "100%" }}>

            {/* Buttons row */}
            <Box width="100%" display="flex" justifyContent="center" sx={{ pb: calcMinWidth ? 1 : 1 * dimensionCard, flexShrink: 0 }}>
                <Box sx={{ display: "flex", gap: 1 }}>
                    {/*Info Icon // Fullscreen Icon */}
                    {infoIconFlag && !play ?
                        (
                            <section data-tour={"single_card_metadata_button"}>
                                <Chip size="small" onClick={(e) => { e.stopPropagation(); setFlipped(true); }} sx={{fontSize: 11, borderRadius: '6px'}} label={"Info"}/>
                            </section>
                        )
                        : (
                            <Chip onClick={handleToggleFullscreen} size="small" label={"Fullscreen"} sx={{fontSize: 11, borderRadius: '6px'}}/>
                        )}

                    {/* Play / Pause Icon */}
                    {!play ?
                        (
                            <Chip onClick={(e) => {
                                trackAction(UI_TRACKING_ACTION.CONTENT_PLAY, {
                                    content_id: card.id,
                                    content_type: clipType === "key_moments" ? TRACKING_CONTENT_TYPE.KEY_MOMENT : TRACKING_CONTENT_TYPE.CLIP,
                                    name: card.title, event_name:
                                    card.eyebrow,
                                    competition_name:card.rubric
                                })
                                setPlay(true);
                                e.stopPropagation();
                                card?.materializations?.length === 0 || !card?.materializations ? jumpPlayerRef.current?.play() : playerRef.current?.play();
                            }} label={"Play"} sx={{fontSize: 11, borderRadius: '6px'}} size="small" />
                        ) : (
                            <Chip sx={{fontSize: 11, borderRadius: '6px'}} onClick={(e) => {
                                trackAction(UI_TRACKING_ACTION.CONTENT_PAUSE, {
                                    content_id: card.id,
                                    content_type: clipType === "key_moments" ? TRACKING_CONTENT_TYPE.KEY_MOMENT : TRACKING_CONTENT_TYPE.CLIP,
                                    name: card.title,
                                    event_name: card.eyebrow,
                                    competition_name:card.rubric
                                })
                                setPlay(false);
                                e.stopPropagation();
                                card?.materializations?.length === 0 || !card?.materializations ? jumpPlayerRef.current?.pause() : playerRef.current?.pause();
                            }} size="small" label={"Pause"}/>
                        )}

                    {/* Edit Icon */}
                    {editFlag &&
                        (
                            <Chip size="small" onClick={onEditClick} label={"Edit"} sx={{fontSize: 11, borderRadius: '6px'}}/>
                        )}

                    {/* Download Icon */}
                    {card.materializations?.length > 0 &&
                        (
                            <Chip onClick={(e) => {
                                e.stopPropagation();
                                enqueueSnackbar("Downloading your video...");
                                downloadVideo(aspectRatio.production_url, (card.title || 'clip') + "_" + getAspectRatio(aspectRatio.aspect_ratio));
                            }} size="small" label={"Download"} sx={{fontSize: 11, borderRadius: '6px'}}/>
                        )}

                    {/* Delete Icon */}
                    {deleteFlag && ((contentType === 'clip') || (contentType !== 'clip' && card.is_manual)) &&
                        <Box>
                            <Chip
                                ref={anchorRef}
                                size="small"
                                onClick={(e) => {e.stopPropagation(); setOpenPopper(true)}}
                                label={"Delete"}
                                sx={{fontSize: 11, borderRadius: '6px'}}
                            />
                            <StyledPopper
                                sx={{maxWidth: 300, maxHeight: 100}}
                                message={`Do you want to delete the ${contentType === 'clip' ? 'clip' : 'key moment'}?`}
                                anchorEl={anchorRef.current}
                                afterResponse={(option)=>{handleDeleteKeyMoment(card.id, option)}}
                                open={openPopper}/>
                        </Box>
                    }

                </Box>
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            {/* Text rows */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: calcMinWidth ? 0.5 : 0.5 * dimensionCard , width: "100%" }}>

                {/* Text row 1 - Rubric & Rating */}
                <Grid container size={{ xs: 12 }} alignItems="flex-end">
                    <Grid size={{ xs: 9 }}>
                        <Typography sx={{ fontSize: calcMinWidth ? 10 : 10*dimensionCard, lineHeight: 1.1, fontFamily: "IBM Plex Sans, sans-serif !important" }}>
                            {card.rubric}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 3 }} sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Tooltip title={card.rating} placement="top">
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <StaticStarRating value={card.rating} fontSize={calcMinWidth ? 10: 10 * dimensionCard} />
                            </Box>
                        </Tooltip>
                    </Grid>
                </Grid>

                {/* Text row 2 - Eyebrow & Play Time */}
                <Grid container size={{ xs: 12 }} alignItems="flex-start">
                    <Grid size={{ xs: 9 }} sx={{ minWidth: 0, overflow: "hidden" }}>
                        <Tooltip title={card.eyebrow} placement="top">
                            <Typography sx={{ fontSize: calcMinWidth ? 12 : 12 * dimensionCard, lineHeight: 1.1, fontWeight: "bold", fontFamily: "IBM Plex Sans, sans-serif !important", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {card.eyebrow}
                            </Typography>
                        </Tooltip>
                    </Grid>
                    {!(card.period === 'PRE_MATCH' || card.title === 'POST_MATCH') &&
                    <Grid size={{ xs: 3 }}>
                        <Typography sx={{ fontSize: calcMinWidth ? 12 : 12 * dimensionCard, lineHeight: 1.1, fontWeight: "bold", textAlign: "right", fontFamily: "IBM Plex Sans, sans-serif !important" }}>
                            {cardFormatPlayTime()}
                        </Typography>
                    </Grid>}
                </Grid>

                {/* Text row 3 - Title & Lead */}
                <Grid container size={{ xs: 12 }} alignItems="flex-start">
                    <Grid size={{ xs: 9 }} sx={{ pr: 1, minWidth: 0, overflow: "hidden" }}>
                        <Tooltip title={clipType === 'clips' ? card.title : (card.title ? intl.formatMessage({ id: card.title, defaultMessage: card.title }) : '') } placement="top">
                            <Typography sx={{ fontSize: calcMinWidth ? 13: 13 * dimensionCard, lineHeight: 1.1, fontFamily: "IBM Plex Sans, sans-serif !important", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {clipType === 'clips' ? card.title : (card.title ? intl.formatMessage({ id: card.title, defaultMessage: card.title }) : '') }
                            </Typography>
                        </Tooltip>
                    </Grid>
                    <Grid size={{ xs: 3 }} sx={{ minWidth: 0, overflow: "hidden" }}>
                        <Tooltip title={card.lead || ''} placement="top">
                            <Typography sx={{ fontSize: calcMinWidth ? 11: 11 * dimensionCard, lineHeight: 1.1, textAlign: "right", fontFamily: "IBM Plex Sans, sans-serif !important", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>
                                {card.lead}
                            </Typography>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );

    return (
        <>
            <section data-tour={"single_card_overview"}>
            <WipeCard
                showedBack={showedBack}
                minWidth={!calcMinWidth ? ((160 * 16) / 9) * dimensionCard : undefined}
                gridSx={calcMinWidth ? { width: "95%", minWidth: "unset" } : undefined}
                outline={ hasWarning ? "3px solid red" : (selected ? (suggested ? "3px solid orange" : "3px solid lightgray") : "3px solid transparent")}
                onClick={toggleSelect ? () => toggleSelect(collectionId + '__' + card.id, multiSelection, card) : undefined}
                renderVideo={renderVideo}
                renderSummary={renderSummary}
                renderDetails={renderDetails}
                renderBack={interactive ? ({ setFlipped, flipped }) => (
                    <>
                        {/* Il tuo normale retro della card */}
                        <ClipCardRetro
                            card={card}
                            clipType={clipType}
                            setFlipped={setFlipped}
                            nameEditDisabled={nameEditDisabled}
                            sortable={sortable}
                        />

                        {flipped && (
                            <Box
                                data-tour={"hidden_element_for_tour"}
                                sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "0px",
                                    height: "0px",
                                    opacity: 0,
                                    pointerEvents: "none",
                                    zIndex: -1
                                }}
                            />
                        )}
                    </>
                ) : undefined}
            />
            {openPlayer && (
                <VideoJSPlayer url={card.url} sourceType={"video/mp4"} autoFullscreen={true} onClose={() => setOpenPlayer(false)} />
            )}
            </section>
        </>
    );
}

export default memo(ClipCard);

// ── Aspect ratio helper (moved here from old wipeCard.jsx) ────────────────────

function getAspectRatio(input, epsilon = 0.01) {
    if (typeof input === 'string' && input.includes(':')) return input;
    const num = parseFloat(input);
    if (isNaN(num) || num <= 0) return "N/D";
    const commonRatios = [
        { ratio: "16:9", val: 16 / 9 },
        { ratio: "9:16", val: 9 / 16 },
        { ratio: "4:3",  val: 4 / 3  },
        { ratio: "3:4",  val: 3 / 4  },
        { ratio: "1:1",  val: 1      },
        { ratio: "21:9", val: 21 / 9 },
    ];
    for (const { ratio, val } of commonRatios) {
        if (Math.abs(num - val) < 0.015) return ratio;
    }
    let x = num, a = Math.floor(x);
    let h1 = 1, h2 = 0, k1 = 0, k2 = 1, iterations = 0;
    while (Math.abs(num - h1 / k1) > epsilon && iterations < 10) {
        let h = a * h1 + h2, k = a * k1 + k2;
        h2 = h1; h1 = h; k2 = k1; k1 = k;
        if (x === a) break;
        x = 1 / (x - a); a = Math.floor(x); iterations++;
    }
    return `${h1}:${k1}`;
}
